import { useLayoutEffect, useMemo, useRef, useState } from "react";

type UseKeyboardOptions = {
  openThresholdPx?: number;
  closeThresholdPx?: number;
  settleMs?: number;
};

type UseKeyboardResult = {
  isKeyboardOpen: boolean;
  keyboardHeight: number;
  viewportHeight: number;
  dismiss: () => void;
};

function isEditable(el: Element | null) {
  if (!el) return false;
  const t = (el as HTMLElement).tagName?.toLowerCase();
  return (
    t === "input" ||
    t === "textarea" ||
    (el as HTMLElement).isContentEditable ||
    (t === "div" && (el as HTMLElement).getAttribute("role") === "textbox")
  );
}

export function useKeyboard({
  openThresholdPx = 90,
  closeThresholdPx = 60,
  settleMs = 80,
}: UseKeyboardOptions = {}): UseKeyboardResult {
  const [isKeyboardOpen, setOpen] = useState(false);
  const [keyboardHeight, setKb] = useState(0);
  const [vvh, setVVH] = useState<number>(
    typeof window !== "undefined"
      ? (window.visualViewport?.height ?? window.innerHeight)
      : 0
  );

  const baseVVH = useRef<number>(0);
  const frozenBaseVVH = useRef<number | null>(null);
  const hasFocus = useRef(false);

  const raf = useRef<number | null>(null);
  const timer = useRef<number | null>(null);
  const schedule = (fn: () => void, delay = 0) => {
    if (raf.current) cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() => {
      if (delay > 0) {
        if (timer.current) clearTimeout(timer.current);
        timer.current = window.setTimeout(fn, delay);
      } else fn();
    });
  };

  const measure = () => {
    const vv = window.visualViewport;
    const h = vv?.height ?? window.innerHeight;

    if (!hasFocus.current) {
      if (h > baseVVH.current) baseVVH.current = h;
      if (baseVVH.current === 0) baseVVH.current = h;
    }

    const base = (hasFocus.current && frozenBaseVVH.current) || baseVVH.current || h;

    let delta = Math.max(0, Math.round(base - h));

    if (!isKeyboardOpen) {
      if (hasFocus.current && delta >= openThresholdPx) {
        setOpen(true);
        setKb(delta);
      } else {
        setOpen(false);
        setKb(0);
      }
    } else {
      if (!hasFocus.current && delta <= closeThresholdPx) {
        setOpen(false);
        setKb(0);
        setTimeout(() => {
          baseVVH.current = window.visualViewport?.height ?? window.innerHeight;
          frozenBaseVVH.current = null;
        }, 120);
      } else {
        setKb(delta);
      }
    }

    setVVH(h);
  };

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    baseVVH.current = window.visualViewport?.height ?? window.innerHeight;

    const onResizeLike = () => schedule(measure, settleMs);
    const onFocusIn = (e: Event) => {
      if (isEditable(e.target as Element)) {
        hasFocus.current = true;
        const h = window.visualViewport?.height ?? window.innerHeight;
        frozenBaseVVH.current = Math.max(h, baseVVH.current);
        schedule(measure, 60);
      }
    };
    const onFocusOut = (e: Event) => {
      if (isEditable(e.target as Element)) {
        hasFocus.current = false;
        setOpen(false);
        setKb(0);
        frozenBaseVVH.current = null;
        schedule(() => {
          baseVVH.current = window.visualViewport?.height ?? window.innerHeight;
          measure();
        }, 120);
      }
    };

    const vv = window.visualViewport;
    vv?.addEventListener("resize", onResizeLike);
    vv?.addEventListener("geometrychange", onResizeLike);
    window.addEventListener("resize", onResizeLike);
    window.addEventListener("orientationchange", () => {
      setOpen(false);
      setKb(0);
      frozenBaseVVH.current = null;
      baseVVH.current = window.visualViewport?.height ?? window.innerHeight;
      schedule(measure, 120);
    });
    window.addEventListener("focusin", onFocusIn);
    window.addEventListener("focusout", onFocusOut);

    schedule(measure, 0);

    return () => {
      vv?.removeEventListener("resize", onResizeLike);
      vv?.removeEventListener("geometrychange", onResizeLike);
      window.removeEventListener("resize", onResizeLike);
      window.removeEventListener("orientationchange", onResizeLike);
      window.removeEventListener("focusin", onFocusIn);
      window.removeEventListener("focusout", onFocusOut);
      if (raf.current) cancelAnimationFrame(raf.current);
      if (timer.current) clearTimeout(timer.current!);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = useMemo(
    () => () => {
      const el = document.activeElement as HTMLElement | null;
      if (el?.blur) el.blur();
    },
    []
  );

  return { isKeyboardOpen, keyboardHeight, viewportHeight: vvh, dismiss };
}
